import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../../../constants';

const SupportEditEventEffect = ({ effect, onChange, onDelete }) => {
    // -- Search State --
    const [searchTerm, setSearchTerm] = useState(effect.skill_id || '');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const wrapperRef = useRef(null);

    // Sync local search term with prop only if we are not actively typing/searching
    useEffect(() => {
        if (!showResults && effect.skill_id !== searchTerm) {
            setSearchTerm(effect.skill_id || '');
        }
    }, [effect.skill_id, searchTerm, showResults]);

    // Close dropdown if clicked outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Debounced Search
    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            // Only search if type is skill and we have a term
            if (effect.type?.toLowerCase() === 'skill' && searchTerm && String(searchTerm).length >= 1 && showResults) {
                setIsSearching(true);
                try {
                    const response = await fetch(`${API_BASE_URL}/api/skills/search?q=${encodeURIComponent(searchTerm)}`);
                    if (response.ok) {
                        const data = await response.json();
                        setSearchResults(data);
                    } else {
                        setSearchResults([]);
                    }
                } catch (err) {
                    console.error("Skill search error", err);
                    setSearchResults([]);
                } finally {
                    setIsSearching(false);
                }
            } else if (!searchTerm) {
                setSearchResults([]);
            }
        }, 400);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, effect.type, showResults]);


    const handleFieldChange = (field, value) => {
        const newEffect = { ...effect, [field]: value };

        // Auto-fill logic for 'restore' type
        if (field === 'type') {
            const lowerValue = value.toLowerCase();
            if (lowerValue === 'restore') {
                newEffect.data = 'Energy';

                // Respect existing bonus if any
                const currentBonus = parseInt(newEffect.bonus);
                if (!isNaN(currentBonus) && currentBonus !== 0) {
                    newEffect.text = `Energy ${currentBonus > 0 ? '+' : ''}${currentBonus}`;
                } else {
                    newEffect.text = 'Energy +1';
                    newEffect.bonus = 1;
                }
            }

            // Clear skill_id if type is not 'skill'
            if (lowerValue !== 'skill') {
                newEffect.skill_id = null;
                setSearchTerm(''); // Clear text too
            }
        }

        // Auto-prefix "(random)" based on toggle state
        if (field === 'random') {
            const prefix = '(random) ';
            const currentText = (newEffect.text || '');

            if (value === true) {
                // Add prefix if missing
                if (!currentText.toLowerCase().startsWith('(random)')) {
                    newEffect.text = prefix + currentText;
                }
            } else {
                // Remove prefix if present
                if (currentText.toLowerCase().startsWith(prefix.trim().toLowerCase())) {
                    // Start with space
                    newEffect.text = currentText.slice(prefix.length);
                } else if (currentText.toLowerCase().startsWith('(random)')) {
                    // Without space
                    newEffect.text = currentText.replace(/^\(random\)\s?/, '');
                }
            }
        }

        // Auto-suffix bonus value
        if (field === 'bonus') {
            const currentText = newEffect.text || '';
            const suffixRegex = / [+-]\d+$/;

            // Interpret 0, empty string, or NaN as null
            let val = parseInt(value);
            if (isNaN(val) || val === 0 || value === '') {
                val = null;
            }
            newEffect.bonus = val; // Force null if 0/empty

            if (val !== null) {
                // Add/Update suffix
                const suffix = val > 0 ? ` +${val}` : ` ${val}`;
                if (suffixRegex.test(currentText)) {
                    newEffect.text = currentText.replace(suffixRegex, suffix);
                } else {
                    newEffect.text = currentText + suffix;
                }
            } else {
                // Remove suffix if value is cleared/zero
                if (suffixRegex.test(currentText)) {
                    newEffect.text = currentText.replace(suffixRegex, '');
                }
            }
        }

        onChange(newEffect);
    };

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        setShowResults(true);

        // Also update the parent ID. If it's a number, save it. If text, save null.
        // This allows users to manually type a known ID too.
        const numVal = parseInt(val);
        if (!isNaN(numVal)) {
            handleFieldChange('skill_id', numVal);
        } else {
            handleFieldChange('skill_id', null);
        }
    };

    const handleSelectSkill = (skill) => {
        // Mass update effect fields
        const updatedEffect = {
            ...effect,
            skill_id: skill.Id_skill,
            data: skill.Skill_name,
            text: `${skill.Skill_name} hint +1`,
            bonus: 1
        };
        onChange(updatedEffect);

        // Update Display Text for search input
        setSearchTerm(skill.Id_skill.toString());
        // Hide Results
        setShowResults(false);
    };

    const getIconPath = (iconId) => `/images/Icon/Skill_Icons/icon_skill_${iconId}.png`;

    return (
        <div className="grid grid-cols-8 gap-2 items-center bg-gray-50 p-2 rounded text-xs overflow-visible" ref={wrapperRef}>
            <div className="col-span-2">
                <input
                    placeholder="Text"
                    value={effect.text || ''}
                    onChange={(e) => handleFieldChange('text', e.target.value)}
                    className="w-full p-1 border rounded"
                />
            </div>
            <div className="col-span-1">
                <input
                    list="effectTypeOptions"
                    placeholder="Type"
                    value={effect.type || ''}
                    onChange={(e) => handleFieldChange('type', e.target.value)}
                    className="w-full p-1 border rounded"
                />
            </div>
            <div className="col-span-1">
                <input
                    placeholder="Data"
                    value={effect.data || ''}
                    onChange={(e) => handleFieldChange('data', e.target.value)}
                    className="w-full p-1 border rounded"
                />
            </div>
            <div className="col-span-1">
                <input
                    placeholder="Val"
                    type="number"
                    // Display empty string for null or 0
                    value={effect.bonus || ''}
                    // Pass raw string to handler to detect clearing
                    onChange={(e) => handleFieldChange('bonus', e.target.value)}
                    onWheel={(e) => e.target.blur()}
                    className="w-full p-1 border rounded"
                />
            </div>

            {/* Skill ID + Search */}
            <div className="col-span-1 relative">
                <input
                    placeholder="Skill ID"
                    value={searchTerm}
                    disabled={effect.type?.toLowerCase() !== 'skill'}
                    onChange={handleSearchChange}
                    onFocus={() => {
                        if (effect.type?.toLowerCase() === 'skill') setShowResults(true);
                    }}
                    className={`w-full p-1 border rounded ${effect.type?.toLowerCase() !== 'skill' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                />

                {/* Search Results Dropdown */}
                {showResults && searchResults.length > 0 && (
                    <div className="absolute top-full left-0 z-50 w-64 bg-white border border-gray-200 rounded shadow-xl max-h-48 overflow-y-auto mt-1">
                        {searchResults.map(skill => (
                            <div
                                key={skill.Id_skill}
                                onClick={() => handleSelectSkill(skill)}
                                className="flex items-center gap-2 p-2 hover:bg-blue-50 cursor-pointer border-b"
                            >
                                <img
                                    src={getIconPath(skill.icon_id)}
                                    alt=""
                                    className="w-6 h-6 object-contain bg-gray-100 rounded"
                                    onError={(e) => e.target.style.display = 'none'}
                                />
                                <div className="overflow-hidden">
                                    <div className="font-bold text-[10px] text-gray-800 truncate">{skill.Skill_name}</div>
                                    <div className="text-[9px] text-gray-500 truncate">{skill.Id_skill} | {skill.Skill_name_jp}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {/* Spinner */}
                {isSearching && (
                    <div className="absolute right-1 top-1.5">
                        <div className="animate-spin h-3 w-3 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                    </div>
                )}
            </div>

            <div className="col-span-1 flex items-center justify-center gap-1">
                <label className="text-[10px] text-gray-500">Rnd</label>
                <button
                    onClick={() => handleFieldChange('random', !effect.random)}
                    className={`
                        px-2 py-1 rounded text-[10px] font-bold border transition-colors w-full
                        ${effect.random
                            ? 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200'
                            : 'bg-gray-100 text-gray-400 border-gray-200 hover:bg-gray-200'
                        }
                    `}
                    title="Toggle Random Effect"
                >
                    {effect.random ? '🎲 Rnd' : 'Fixed'}
                </button>
            </div>
            <div className="col-span-1 flex justify-end">
                <button onClick={onDelete} className="text-red-400 hover:text-red-600 font-bold">×</button>
            </div>
        </div>
    );
};

export default SupportEditEventEffect;
